import { Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  EmptyItem,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { apiPostFiles, postApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import { useTranslation } from '../../contexts/translation';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { convertMinsToHrsMins } from '../../pages/Tasks/Columns';
import { Asset } from '../../types/asset.types';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { Customer } from '../../types/customer.types';
import {
  CustomerProject,
  CustomerProjectBillingStatus,
  CustomerProjectBillingStatusTranslation,
} from '../../types/customerProject.types';
import { LocationState } from '../../types/locationState.types';
import { Person } from '../../types/person.types';
import { TaskStatus } from '../../types/TaskStatus';
import { getColorClass, getUserColorClass, UserColor } from '../../types/userColor.types';
import { WorkActivity } from '../../types/workActivity.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import {
  dateFieldOptionsWithSerialization,
  dateTimeFieldOptions,
  dateTimeFieldOptionsWithSerialization,
} from '../../utils/dates.types';
import { dxSelectBoxConfig } from './TasksForm';
import './TasksServiceDesk.scss';

interface CreateFormData {
  requestorName: string;
  requestorEmail: string;
  requestorPhone?: string;

  customerId: number | null;
  customerProjectId: number | null;
  typeId: number | null;
  topLevelAssetId: number | null;
  assetId: number | null;
  capacityCategoryId: number | null;
  responsiblePersonId: number | null;
  requiredDate: Date | null | string;
  taskName: string;
  description: string;
  creationTime: Date | null | string;
  billingStatus: null | string;
}

interface Props {
  defaultValues: CreateFormData;
  enableSaveTemplate?: boolean;
  templateDetailMode?: boolean;
}

export const TasksServiceDeskCreateForm = ({
  defaultValues,
  enableSaveTemplate,
  templateDetailMode,
}: Props) => {
  const { assetClass } = useAssetClass();
  const { user } = useAuth();
  const history = useHistory();
  const { translate } = useTranslation();
  const transFormName = 'Task.CreateServiceDesk';
  const { state }: { state: LocationState | undefined } = useLocation();

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    defaultValues.customerId || null,
  );
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(
    defaultValues.topLevelAssetId || null,
  );
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(
    defaultValues.typeId || null,
  );
  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const [activityStartedSet, setActivityStartedSet] = useState(false);
  const [isShowDowntime, setShowDowntime] = useState<boolean>(false);
  const [projectColorClass, setProjectColorClass] = useState<string | null>();

  const formRef = useRef<any>();
  const formData = useRef<any>(defaultValues);

  const backRoute = '';
  const [
    prompt,
    setDirty,
    setClean,
    handleBackButton,
    handleHistoryGoBackButton,
    handleDirtyWithCallback,
    isDirty,
  ] = useUnsavedChangesWarning(backRoute, (backRouteObject) => {
    if (!formRef) return;
    if (formRef.current!.instance.validate().isValid)
      onSubmit(null).then((result) => {
        if (result)
          if (!backRouteObject) history.goBack();
          else history.push(backRouteObject);
        // if (result) history.push(`/tasks/edit/${result}`);
        else setDirty();
      });
  });

  const { data: customers } = useSWR<Customer[]>(`${API_URL.CUSTOMER_LIST}`, fetcher, swrConfig);
  const { data: projects } = useSWR<CustomerProject[]>(
    selectedCustomerId ? `${API_URL.CUSTOMER_PROJECT_LIST}?parentId=${selectedCustomerId}` : null,
    fetcher,
    swrConfig,
  );

  const { data: documentTypes } = useSWR<any[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );
  const { data: capacityCategories } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: topLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClass}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );
  const { data: assets } = useSWR<Asset[]>(
    selectedTopLevelAssetId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}&parentId=${selectedTopLevelAssetId}&assetToInclude=${selectedTopLevelAssetId}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: assetsAssignToCustomer } = useSWR<Asset[]>(
    selectedCustomerId
      ? `${API_URL.ASSET_LIST_FOR_CUSTOMER}?customerId=${selectedCustomerId}`
      : null,
    fetcher,
    swrConfig,
  );
  const { data: responsiblePersons } = useSWR<Person[]>(
    API_URL.PERSON_LIST_WITH_CAPACITY_CAT,
    fetcher,
  );
  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );
  const { data: statuses } = useSWR<TaskStatus[]>(
    selectedDocumentTypeId
      ? `${API_URL.TASK_PROCESS_CONFIG_LIST_BY_DOC_TYPE}?documentTypeId=${selectedDocumentTypeId}`
      : null,
    fetcher,
    swrConfig,
  );
  const { data: workActivities } = useSWR<WorkActivity[]>(
    `${API_URL.WORK_ACTIVITY_LIST_BY_ASSET_CLASS}?assetClassId=${assetClass}&visibleOnly=true`,
    fetcher,
  );

  // Načetly se customers, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!customers || customers.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('customerId', customers[0].id);
  }, [formRef, customers]);

  // Načetly se projekty (po výběru customera), automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!projects || projects.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('customerProjectId', projects[0].id);
  }, [formRef, projects]);

  // Načetly se types, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!documentTypes || documentTypes.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('typeId', documentTypes[0].id);
  }, [formRef, documentTypes]);

  // Načetly se categories, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!capacityCategories || capacityCategories.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('capacityCategoryId', capacityCategories[0].id);
  }, [formRef, capacityCategories]);

  // Načetly se assety, pokud je pouze jeden, vybereme
  useEffect(() => {
    if (!assets || assets.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('assetId', assets[0].id);
  }, [formRef, assets]);

  // Načetly se aktivity, vybereme první
  useEffect(() => {
    if (!workActivities || workActivities.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('workActivityId', workActivities[0].id);
  }, [formRef.current, workActivities]);

  useEffect(() => {
    if (!defaultValues.typeId) return;
    const documentType = documentTypes?.find((x) => x.id === defaultValues.typeId);
    if (documentType) setShowDowntime(documentType.showDowntime);
    //setShowDowntime(true);
  }, [defaultValues.typeId, documentTypes]);

  useEffect(() => {
    const instance = formRef.current?.instance;
    if (!instance) return;
    if (defaultValues.customerProjectId)
      handleCustomerProjectChanged(defaultValues.customerProjectId);
  }, [userColors, documentTypes, assets, projects, defaultValues.customerProjectId]);

  useEffect(() => {
    if (!projects || !defaultValues.customerProjectId) return;
    const color = projects.find((x) => x.id === defaultValues.customerProjectId);
    if (color) {
      // @ts-ignore
      setProjectColorClass(getColorClass(color.color));
    }
  }, [defaultValues.customerProjectId, projects]);

  useEffect(() => {
    if (!topLevelAssets || !defaultValues.assetId || !assets) return;
    const instance = formRef.current?.instance;
    if (!instance) return;

    const asset = assets.find((x) => x.id === defaultValues.assetId);
    if (!asset) return;
    formRef.current.instance.updateData('topLevelAssetId', asset.topLevelParenId);
  }, [defaultValues.assetId, topLevelAssets, assets]);

  // Při změně assetu se vybere parent
  const handleAssetChanged = useCallback(
    (assetId: number) => {
      if (selectedTopLevelAssetId || !assets) return;
      const instance = formRef.current?.instance;
      if (!instance) return;

      const asset: Asset = assets.filter((x) => x.id === assetId)[0];
      if (!asset) return;
      formRef.current.instance.updateData('topLevelAssetId', asset.topLevelParenId);
    },
    [selectedTopLevelAssetId, assets],
  );

  // Při zěmně projektu se předvyplní billing status
  const handleCustomerProjectChanged = useCallback(
    (customerProjectId: number) => {
      if (!projects || !customerProjectId) return;
      const instance = formRef.current?.instance;
      if (!instance) return;

      const project = projects.filter((x) => x.id === customerProjectId)[0];
      if (!project) return;
      formRef.current.instance.updateData('billingStatus', project.defaultBillingStatus);
      if (project.assetId) {
        formRef.current.instance.updateData('assetId', project.assetId);
        handleAssetChanged(project.assetId);
      }
    },
    [projects],
  );

  const onSubmit = async (e: any, targetTaskStatusIdForCreate?: number) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;
    let resultId: number | null = null;

    setClean();
    resultId = await submitPost(targetTaskStatusIdForCreate);

    if (resultId) history.push(`/tasks/edit/${resultId}`);
    else setDirty();

    return resultId;
  };

  const submitPost = useCallback(async (targetTaskStatusIdForCreate?: number) => {
    let resultId: number | null = null;

    await postApi({
      url: API_URL.TASK_POST,
      data: targetTaskStatusIdForCreate
        ? { ...formData.current, targetTaskStatusIdForCreate: targetTaskStatusIdForCreate }
        : formData.current,
      callAfterSuccess: async (data) => {
        resultId = data.id;

        if (formData.current && formData.current.files && formData.current.files.length > 0)
          await Promise.all(
            formData.current.files.map(async (file: File) => {
              if (file.size <= 30000000) await fileUpload(file, data.id);
            }),
          );

        // Worksheet
        if (formData.current.duration) {
          // založím task, vezmu jeho čas,. odečtu trvání = začátek výkazu
          // Rozparsování časové části (HH:mm)
          const durationParts = formData.current.duration.split(':');
          const durationHours = parseInt(durationParts[0], 10);
          const durationMinutes = parseInt(durationParts[1], 10);

          const worksheetFormData = {
            taskId: data.id,
            workActivityId: formData.current.workActivityId,
            workSheetStatusId: 20,
            operatorId: formData.current.responsiblePersonId,
            activityStarted: moment(formData.current.activityStarted).format('YYYY-MM-DDTHH:mm'),
            activityFinished: moment(formData.current.activityStarted)
              .add(durationHours, 'hours')
              .add(durationMinutes, 'minutes')
              .format('YYYY-MM-DDTHH:mm'),
            taskComplete: false,
          };

          await postApi({
            url: `${API_URL.WORKSHEET_POST2}?autoFinishOlder=false`,
            data: worksheetFormData,
          });
        }
      },
    });

    return resultId;
  }, []);

  const fileUpload = useCallback(async (file: File, taskId: number) => {
    try {
      const formData = new FormData();
      // can upload only 1 file
      formData.append('files', file);
      await apiPostFiles({
        url: `${API_URL.FILE_UPLOAD}?entity=Task&entityId=${taskId}`,
        data: formData,
      });
    } catch (err) {}
  }, []);

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  const submitFormWithTaskState = (targetTaskStatusIdForCreate: number) => {
    if (formRef.current.instance.validate().isValid) onSubmit(null, targetTaskStatusIdForCreate);
  };

  const projectsWithGroupName = projects?.map((project) => ({
    ...project,
    groupName: project.projectStatusIsActive
      ? translate!('Active projects', transFormName)
      : translate!('Inactive projects', transFormName),
  }));

  const saveTemplate = useCallback(async () => {
    if (!user) return;

    await postApi({
      url: API_URL.TASK_TEMPLATE_CREATE_UPDATE,
      data: {
        ...formData.current,
        id: 0,
        userId: user.personalNumber,
        assetClassId: assetClass,
        inboxEmail: null,
        inboxFolderName: null,
        requestorEmail: null,
      },
      callAfterSuccess: async (data) => {},
    });
  }, [formData, user, assetClass, formRef]);

  const onHtmlEditorContentReady = useCallback((e: any) => {
    const container = e.element.closest('.dx-htmleditor');
    if (container) {
      container.style.resize = 'vertical';
      container.style.overflow = 'auto';
      container.style.minHeight = '280px';
    }
  }, []);

  const onHtmlEditorFocusOut = useCallback(() => {
    if (!isDirty) setDirty();
  }, [isDirty, setDirty]);

  const htmlEditorOptions = useMemo(
    () => ({
      height: 280,
      resizable: { handles: 's' },
      toolbar: {
        items: [
          'undo',
          'redo',
          'bold',
          'italic',
          'underline',
          'strike',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'alignJustify',
          'orderedList',
          'bulletList',
          'fontSize',
          'fontColor',
          'color',
          'background',
          'link',
          'clear',
        ],
      },
      mediaResizing: { enabled: true },
      onContentReady: onHtmlEditorContentReady,
      onFocusOut: onHtmlEditorFocusOut,
    }),
    [onHtmlEditorContentReady],
  );

  if (!userColors || !documentTypes || !assets || (!projects && selectedCustomerId))
    return <Loader />;

  return (
    <>
      <form onSubmit={onSubmit}>
        <Form
          formData={formData.current}
          ref={formRef}
          colCount={3}
          onFieldDataChanged={(e) => {
            if (e?.dataField === 'description') return;
            if (!isDirty) setDirty();
          }}
        >
          <SimpleItem
            dataField={'customerId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: customers,
              onValueChanged: (e: any) => setSelectedCustomerId(e.value),
            }}
          >
            <Label text={translate!('Customer', transFormName)} />
          </SimpleItem>
          <SimpleItem
            cssClass={`dropdown-user-color ${projectColorClass ? projectColorClass : ''}`}
            dataField={'customerProjectId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: new DataSource({
                store: projectsWithGroupName,
                group: {
                  selector: 'groupName',
                  desc: false,
                },
              }),
              readOnly: !selectedCustomerId,
              grouped: true,
              groupExpr: 'groupName',
              onValueChanged: (e: any) => {
                handleCustomerProjectChanged(e.value);

                if (!projects) return;
                const color = projects.find((x) => x.id === e.value);
                if (color) {
                  // @ts-ignore
                  setProjectColorClass(getColorClass(color.color));
                }
              },
              itemTemplate: (data: any, _index: number, element: any) => {
                if (!projects) return;
                const color = projects.find((x) => x.id === data.id);
                if (color)
                  element.innerHTML = `<div style="color: #${color.color}">${data.name}</div>`;
                else element.innerHTML = `<div>${data.name}</div>`;
              },
            }}
          >
            <Label text={translate!('Project', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'typeId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: documentTypes,
              onValueChanged: (e: any) => {
                const documentType = documentTypes.find((x) => x.id === e.value);
                if (documentType) setShowDowntime(documentType.showDowntime);
                else setShowDowntime(false);
                setSelectedDocumentTypeId(e.value);
              },
            }}
          >
            <Label text={translate!('Type', transFormName)} />
            <RequiredRule message={translate!('Type is required', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'topLevelAssetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: selectedCustomerId
                ? topLevelAssets?.filter((asset) =>
                    assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                  )
                : topLevelAssets,
              onValueChanged: (e: any) => setSelectedTopLevelAssetId(e.value),
            }}
          >
            <Label text={translate!('Parent asset', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'assetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: selectedCustomerId
                ? assets.filter((asset) =>
                    assetsAssignToCustomer?.some((assigned) => assigned.id === asset.id),
                  )
                : assets,
              onValueChanged: (e: any) => handleAssetChanged(e.value),
            }}
          >
            <Label text={translate!('Asset', transFormName)} />
            <RequiredRule message={translate!('Asset is required', transFormName)} />
          </SimpleItem>

          <SimpleItem
            dataField={'capacityCategoryId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: capacityCategories,
            }}
          >
            <Label text={translate!('Capacity category', transFormName)} />
            <RequiredRule message={translate!('Capacity category is required', transFormName)} />
          </SimpleItem>
          <SimpleItem
            cssClass={`dropdown-user-color ${userColorClass}`}
            dataField="responsiblePersonId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'personalNumber',
              displayExpr: 'fullName',
              items: responsiblePersons,
              onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
              itemTemplate: (data: any, _index: number, element: any) => {
                const color = userColors.find((x) => x.userId === data.personalNumber);
                if (color)
                  element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
                else element.innerHTML = `<div>${data.fullName}</div>`;
              },
            }}
          >
            <Label text={translate!('Responsible person', transFormName)} />
            {activityStartedSet && (
              <RequiredRule message={translate!('Responsible person is required', transFormName)} />
            )}
          </SimpleItem>
          {!templateDetailMode && (
            <SimpleItem
              dataField="requiredDate"
              editorType="dxDateBox"
              editorOptions={{
                ...dateFieldOptionsWithSerialization,
                min: new Date(),
              }}
            >
              <Label text={translate!('Required date', transFormName)} />
            </SimpleItem>
          )}

          {!templateDetailMode && (
            <SimpleItem
              dataField="creationTime"
              editorType="dxDateBox"
              editorOptions={{
                ...dateTimeFieldOptionsWithSerialization,
              }}
            >
              <Label text={translate!('Date of creation', transFormName)} />
              <RequiredRule message={translate!('Date of creation is required', transFormName)} />
            </SimpleItem>
          )}

          {templateDetailMode && <EmptyItem />}
          {templateDetailMode && <EmptyItem />}

          <GroupItem cssClass={'requestorGroup'}>
            <SimpleItem dataField="requestorName" editorType="dxTextBox">
              <Label text={translate!('Requestor name', transFormName)} />
            </SimpleItem>
            <SimpleItem dataField="requestorEmail" editorType="dxTextBox">
              <Label text={translate!('Requestor email', transFormName)} />
              <CustomRule
                message={translate!('Enter valid email', transFormName)}
                validationCallback={(e) => {
                  return !e.value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.value);
                }}
              />
            </SimpleItem>
            <SimpleItem dataField="requestorPhone" editorType="dxTextBox">
              <Label text={translate!('Requestor phone', transFormName)} />
              <CustomRule
                message={translate!('Enter valid phone number', transFormName)}
                validationCallback={(e) => {
                  return (
                    !e.value ||
                    /^(\+?[0-9]{1,4}( ?))?[0-9]{3}( ?)[0-9]{3}( ?)[0-9]{3}$/.test(e.value)
                  );
                }}
              />
            </SimpleItem>
          </GroupItem>

          <GroupItem colCount={1}>
            <SimpleItem
              dataField={'billingStatus'}
              editorType={'dxSelectBox'}
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                dataSource: Object.values(CustomerProjectBillingStatus).map(
                  (status: CustomerProjectBillingStatus) => {
                    return {
                      id: status,
                      name: translate!(
                        CustomerProjectBillingStatusTranslation[status],
                        `CustomersProjects.Status`,
                      ),
                    };
                  },
                ),
              }}
            >
              <Label text={translate!('Billing status', transFormName)} />
            </SimpleItem>
            <GroupItem visible={isShowDowntime}>
              <SimpleItem
                dataField="downtimeFrom"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateTimeFieldOptionsWithSerialization,
                }}
              >
                <Label text={translate!('Downtime from', transFormName)} />
                <RequiredRule message={translate!('Downtime from required', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="downtimeTo"
                editorType="dxDateBox"
                editorOptions={{
                  ...dateTimeFieldOptionsWithSerialization,
                }}
              >
                <Label text={translate!('Downtime to', transFormName)} />
              </SimpleItem>
            </GroupItem>
          </GroupItem>

          <GroupItem>
            <SimpleItem
              dataField={'workActivityId'}
              editorType={'dxSelectBox'}
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                dataSource: workActivities,
              }}
            >
              <Label text={translate!('Work activity', transFormName)} />
              {activityStartedSet && (
                <RequiredRule message={translate!('Work activity is required', transFormName)} />
              )}
            </SimpleItem>
            <SimpleItem
              dataField="activityStarted"
              editorType="dxDateBox"
              editorOptions={{
                ...dateTimeFieldOptions,
                // min: !disableWorksheetTaskTimeValidation
                //   ? moment(defaultFormData.emailReceived)
                //   : undefined,
                max: moment(),
                onValueChanged: (e) => {
                  e.component.option('value', e.value);
                  if (e.event) setActivityStartedSet(true);
                },
              }}
            >
              <Label text={translate!('Activity started', transFormName)} />
            </SimpleItem>
            <SimpleItem
              dataField="duration"
              editorType="dxTextBox"
              editorOptions={{
                onValueChanged: (e) => {
                  if (e.value === undefined) return;

                  // Already converted to HH:MM format — skip re-processing
                  if (/^\d+:\d{2}$/.test(e.value.trim())) return;

                  if (Number.isNaN(Number(e.value.replace(',', '.').replace(':', '.')))) {
                    return;
                  }

                  const newValue = e.value.trim();
                  let value = newValue.replace(',', '.').replace(':', '.');
                  if (value.includes('.')) {
                    const valueArr = value.split('.');
                    value = convertMinsToHrsMins(Number(valueArr[0]) * 60 + Number(valueArr[1]));
                  } else {
                    value = convertMinsToHrsMins(value);
                  }

                  if (!activityStartedSet) {
                    const durationParts = value.split(':');
                    const durationHours = parseInt(durationParts[0], 10);
                    const durationMinutes = parseInt(durationParts[1], 10);

                    const activityStarted = moment()
                      .subtract(durationHours, 'hours')
                      .subtract(durationMinutes, 'minutes')
                      .format('YYYY-MM-DDTHH:mm');

                    formRef.current.instance.updateData('activityStarted', activityStarted);
                    setActivityStartedSet(true);
                  }

                  formRef.current.instance.updateData('duration', value);
                },
              }}
            >
              <Label text={translate!('Duration', transFormName)} />
              <CustomRule
                validationCallback={(e) => {
                  if (e.value === undefined) return true;
                  if (Number.isNaN(Number(e.value.replace(',', '.').replace(':', '.'))))
                    return false;
                  return true;
                }}
              />
            </SimpleItem>
          </GroupItem>
          {!templateDetailMode && (
            <SimpleItem dataField="taskName" editorType="dxTextBox" colSpan={2}>
              <Label text={translate!('Name', transFormName)} />
              <RequiredRule message={translate!('Name is required', transFormName)} />
            </SimpleItem>
          )}

          {!templateDetailMode && <EmptyItem />}

          <SimpleItem
            dataField="description"
            editorType="dxHtmlEditor"
            colSpan={3}
            editorOptions={htmlEditorOptions}
          >
            <Label location={'top'} text={translate!('Description', transFormName)} />
          </SimpleItem>

          {templateDetailMode && (
            <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
              <ButtonOptions
                width={'100px'}
                text={translate!('Close', transFormName)}
                type={'normal'}
                useSubmitBehavior={false}
                // onClick={() =>
                //   history.push({
                //     pathname: '/task-templates',
                //     state: { loadSettingFromSession: true },
                //   })
                // }
                onClick={handleBackButton}
              />
            </ButtonItem>
          )}

          {!templateDetailMode && (
            <GroupItem colCount={7} colSpan={3}>
              <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                <ButtonOptions
                  width={'100%'}
                  text={translate!('Close', transFormName)}
                  type={'normal'}
                  useSubmitBehavior={false}
                  // onClick={() =>
                  //   history.push({
                  //     pathname: backRoute,
                  //     state: { loadSettingFromSession: true },
                  //   })
                  // }
                  onClick={handleBackButton}
                />
              </ButtonItem>
              {!statuses || statuses.length <= 1 ? (
                <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                  <ButtonOptions
                    width={'100%'}
                    text={translate!('Save', transFormName)}
                    type={'success'}
                    useSubmitBehavior
                  />
                </ButtonItem>
              ) : (
                statuses.map((status, i) => {
                  return (
                    <ButtonItem
                      key={i}
                      colSpan={1}
                      verticalAlignment={'center'}
                      horizontalAlignment={'left'}
                    >
                      <ButtonOptions
                        width={'100%'}
                        text={`${translate!('Save', transFormName)} - ${
                          status.statusToNameTranslated
                        }`}
                        type={'success'}
                        useSubmitBehavior={false}
                        onClick={() => submitFormWithTaskState(status.statusToId)}
                      />
                    </ButtonItem>
                  );
                })
              )}
              {enableSaveTemplate && (
                <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
                  <ButtonOptions
                    width={'100%'}
                    text={translate!('Save as template', transFormName)}
                    type={'normal'}
                    useSubmitBehavior={false}
                    onClick={() => saveTemplate()}
                  />
                </ButtonItem>
              )}
            </GroupItem>
          )}

          {!templateDetailMode && (
            <SimpleItem
              dataField="files"
              editorType="dxFileUploader"
              editorOptions={{
                accept: '*',
                uploadMode: 'useForm',
                multiple: true,
                maxFileSize: 29884416,
                onValueChanged: (e: any) => {
                  e.value.forEach((file) => {
                    if (file.size > 30000000)
                      notify(`${file.name} - ${translate!('File is too large', '')}`, 'error');
                  });
                },
              }}
            >
              <Label text="Files" location="top" />
            </SimpleItem>
          )}
        </Form>
      </form>
      {prompt}
    </>
  );
};
